import { useState, useEffect, useCallback, useRef } from 'react';
import MatchRoundService from '../services/MatchRoundService';
import ChatService from '../services/ChatService';
import { resolveAvatarUrl } from '../utils/api';
import triggerHaptic from '../utils/haptics';

/**
 * Lädt und verwaltet die Match-Runde ("Deine 6 für heute").
 *
 * Gegenstück zu useProfileLoader, aber mit anderem Vertrag: keine Paginierung,
 * kein Nachladen, kein Infinite Scroll — die Runde hat sechs Karten und ein Ende.
 * useProfileLoader bleibt daneben bestehen und bedient weiterhin die Suche.
 */

// Dieselbe Normalisierung wie in useProfileLoader: das Backend liefert Skills je
// nach Endpoint unter drei verschiedenen Namen.
const normalizeProfiles = (profiles = []) => {
  if (!Array.isArray(profiles)) return [];

  return profiles.map(profile => {
    const offered = profile.tenant_skills_OfferedSkills || profile.offeredSkills || profile.offers || [];
    const sought = profile.tenant_skills_SoughtSkills || profile.soughtSkills || profile.seeks || [];

    const toNames = list => (Array.isArray(list)
      ? list.map(skill => (typeof skill === 'string' ? skill : skill.name || '')).filter(Boolean)
      : []);

    const normalizedOffers = toNames(offered);
    const normalizedSeeks = toNames(sought);

    return {
      ...profile,
      offeredSkills: normalizedOffers,
      soughtSkills: normalizedSeeks,
      offers: normalizedOffers,
      seeks: normalizedSeeks,
      name: profile.displayName || profile.name,
      bio: profile.bio || profile.aboutMe || '',
      aboutMe: profile.aboutMe || profile.bio || '',
      image: resolveAvatarUrl(profile.image || profile.avatar || '', 'card'),
      avatar: resolveAvatarUrl(profile.avatar || profile.image || '', 'thumb'),
      matchScore: profile.matchScore || 0
    };
  });
};

// Wartezeiten fuer die Wiederholung, wenn der Status-Batch scheitert oder
// unvollstaendig ist (z. B. erste Anfrage nach der Rueckkehr in die iOS-App).
export const STATUS_RETRY_DELAYS_MS = [2000, 6000];

const statusKey = profile => profile?.userId || profile?.id;

/**
 * Traegt frische Status in die Karten ein. Wer im Ergebnis fehlt, behaelt seinen
 * bisherigen Status: Ein fehlender Eintrag heisst "unbekannt", nicht "nicht
 * vernetzt". Bis November 2026 setzte der Hook hier 'none' — scheiterte der
 * Batch (ChatService liefert dann `{}`), stand bei allen Karten "Vernetzen".
 */
export const mergeConnectionStatuses = (list, statuses) => {
  if (!statuses || typeof statuses !== 'object') return list;
  let changed = false;
  const merged = list.map(profile => {
    const fresh = statuses[statusKey(profile)];
    if (!fresh || fresh === profile.connectionStatus) return profile;
    changed = true;
    return { ...profile, connectionStatus: fresh };
  });
  return changed ? merged : list;
};

export const useMatchRound = ({ authUser, enabled = true }) => {
  const [profiles, setProfiles] = useState([]);
  const [round, setRound] = useState(null);
  const [meta, setMeta] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);
  const [error, setError] = useState(null);

  // Verhindert doppelte Ziehungen, wenn schnell hintereinander getippt wird —
  // erste Verteidigungslinie vor Server-Sperre und Rate-Limit.
  const drawInFlightRef = useRef(false);

  // Status-Nachladen: aktuelle Karten (fuer Wiederholung und Rueckkehr in die
  // App), Laufnummer gegen veraltete Antworten, Timer der Wiederholung.
  const profilesRef = useRef([]);
  const statusRequestRef = useRef(0);
  const statusRetryRef = useRef(null);
  const mountedRef = useRef(true);
  const authUserIdRef = useRef(authUser?.id);
  authUserIdRef.current = authUser?.id;

  const setRoundProfiles = useCallback((next) => {
    profilesRef.current = next;
    setProfiles(next);
  }, []);

  /**
   * Fuellt fehlende Status mit dem zuletzt bekannten Stand: erst aus den Karten,
   * die schon zu sehen waren, dann aus dem Speicher des ChatService. So springt
   * der Knopf beim Neuaufbau nicht erst auf "Vernetzen". Der Batch danach
   * korrigiert, falls sich etwas geaendert hat.
   */
  const withKnownStatus = useCallback((list) => {
    const previous = new Map(profilesRef.current.map(p => [statusKey(p), p.connectionStatus]));
    return list.map(profile => {
      if (profile.connectionStatus) return profile;
      const key = statusKey(profile);
      // Aus dem Speicher nur "vernetzt": Eine offene Anfrage kann inzwischen
      // erledigt sein, und ein veraltetes "Anfrage annehmen" scheitert beim Tippen.
      const cached = ChatService.peekConnectionStatus(key);
      const known = previous.get(key) || (cached?.status === 'connected' ? cached : null);
      return known ? { ...profile, connectionStatus: known } : profile;
    });
  }, []);

  /**
   * Holt den Verbindungsstatus (vernetzt / Anfrage offen) fuer die Karten.
   * Nicht blockierend fuer die Darstellung: die Karten sind schon sichtbar,
   * der Knopf aktualisiert sich nach. Scheitert der Abruf oder fehlen Personen
   * im Ergebnis, folgen bis zu zwei Wiederholungen.
   */
  const refreshConnectionStatus = useCallback(async (attempt = 0) => {
    clearTimeout(statusRetryRef.current);
    const ownId = authUserIdRef.current;
    const ids = [...new Set(profilesRef.current.map(statusKey))]
      .filter(id => id && id !== ownId);
    if (ids.length === 0) return;

    statusRequestRef.current += 1;
    const requestId = statusRequestRef.current;
    let statuses = {};
    try {
      statuses = (await ChatService.getConnectionStatusBatch(ids)) || {};
    } catch {
      statuses = {};
    }
    // Eine neuere Abfrage laeuft schon oder die Ansicht ist weg.
    if (!mountedRef.current || requestId !== statusRequestRef.current) return;

    setRoundProfiles(mergeConnectionStatuses(profilesRef.current, statuses));

    const incomplete = ids.some(id => !statuses[id]);
    if (incomplete && attempt < STATUS_RETRY_DELAYS_MS.length) {
      statusRetryRef.current = setTimeout(
        () => refreshConnectionStatus(attempt + 1),
        STATUS_RETRY_DELAYS_MS[attempt]
      );
    }
  }, [setRoundProfiles]);

  const applyResult = useCallback(async (result) => {
    const normalized = withKnownStatus(normalizeProfiles(result.profiles || []));
    setRoundProfiles(normalized);
    setRound(result.round || null);
    setMeta(result.meta || {});
    setError(null);

    await refreshConnectionStatus();
  }, [withKnownStatus, setRoundProfiles, refreshConnectionStatus]);

  /** Die aktuelle Runde laden — verbraucht kein Kontingent. */
  const loadRound = useCallback(async () => {
    if (!enabled || !authUser?.id) return;
    setIsLoading(true);
    try {
      const result = await MatchRoundService.getRound();
      await applyResult(result);
    } catch (loadError) {
      setError(loadError?.message || 'Deine Runde konnte nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, authUser?.id, applyResult]);

  /**
   * Neue Runde ziehen. Wird sowohl vom Knopf als auch von der Pull-Geste genutzt.
   * @returns {Promise<boolean>} true, wenn tatsächlich gezogen wurde
   */
  const drawNextRound = useCallback(async () => {
    if (!enabled || !authUser?.id || drawInFlightRef.current) return false;

    drawInFlightRef.current = true;
    setIsDrawing(true);
    try {
      const result = await MatchRoundService.drawNextRound();
      await applyResult(result);
      triggerHaptic('success');
      return true;
    } catch (drawError) {
      if (drawError?.limitReached) {
        // Kein Fehlerzustand, sondern ein normaler Endpunkt des Tages: nur den
        // Zähler aktualisieren, damit die Schlusskarte den Countdown zeigt.
        if (drawError.round) setRound(drawError.round);
        return false;
      }
      if (drawError?.conflict) {
        // Eine parallele Anfrage war schneller — die gültige Runde nachladen.
        await loadRound();
        return false;
      }
      setError(drawError?.message || 'Die neue Runde konnte nicht geladen werden.');
      return false;
    } finally {
      drawInFlightRef.current = false;
      setIsDrawing(false);
    }
  }, [enabled, authUser?.id, applyResult, loadRound]);

  /**
   * "Später zeigen". Die Karte verschwindet sofort aus der laufenden Runde,
   * es rückt aber nichts nach — die Runde wird kürzer. Nachrücken würde die
   * Begrenzung aushebeln und die Geste zum Nachschub-Hebel machen.
   */
  const snoozeProfile = useCallback(async (targetUserId) => {
    if (!targetUserId) return;
    setRoundProfiles(profilesRef.current.filter(
      profile => (profile.userId || profile.id) !== targetUserId
    ));
    try {
      await MatchRoundService.snooze(targetUserId);
    } catch {
      // Die Karte bleibt bis zum nächsten Laden weg. Sie zurückspringen zu
      // lassen wäre verwirrender als der stille Fehlschlag.
    }
  }, [setRoundProfiles]);

  /**
   * Nach "Blockieren": Karte sofort aus der Runde nehmen. Der Server filtert
   * Blockierte beim naechsten Laden ohnehin heraus; hier geht es nur darum,
   * dass die Karte nicht bis zum Neuladen stehen bleibt. Wie beim Snooze
   * rueckt nichts nach.
   */
  const removeProfile = useCallback((targetUserId) => {
    if (!targetUserId) return;
    setRoundProfiles(profilesRef.current.filter(
      profile => (profile.userId || profile.id) !== targetUserId
    ));
  }, [setRoundProfiles]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    if (authUser?.id) loadRound();
  }, [enabled, authUser?.id, loadRound]);

  // Rueckkehr in die App (App-Wechsel, Sperrbildschirm, Push): nur den Status
  // neu holen, nicht die Runde. Wer vernetzt ist, kann sich im Hintergrund
  // geaendert haben; welche Personen in der Runde stehen, bleibt unberuehrt.
  useEffect(() => {
    if (!enabled) return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshConnectionStatus();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, refreshConnectionStatus]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(statusRetryRef.current);
    };
  }, []);

  return {
    profiles,
    round,
    meta,
    isLoading,
    isDrawing,
    error,
    loadRound,
    drawNextRound,
    snoozeProfile,
    removeProfile,
    // Die Runde ist verbraucht, wenn nichts mehr gezogen werden darf.
    canDraw: round?.canDrawNow ?? true
  };
};

export default useMatchRound;
